import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FiCheck, FiSearch, FiUserCheck, FiUserX } from "react-icons/fi";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";

// Aprobar, suspender y reactivar clientes desde la página principal del entrenador.
const nombre = (u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;
const fecha = (u) => (u.createdAt ? new Date(u.createdAt).toLocaleDateString("es-ES") : "");

const fila = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "10px 0",
  borderTop: "1px solid rgba(127,127,127,.18)",
};

export default function GestionAccesos() {
  const [usuarios, setUsuarios] = useState(null);
  const [error, setError] = useState("");
  const [ocupado, setOcupado] = useState(null);
  const [buscar, setBuscar] = useState("");
  const [verTodos, setVerTodos] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      const lista = res.data.users || res.data;
      setUsuarios(lista.filter((u) => u.role !== "admin"));
      setError("");
    } catch {
      setError("No se pudo cargar la lista de clientes.");
      setUsuarios([]);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = async (u, accion) => {
    if (accion === "suspend" && !window.confirm(`¿Suspender a ${nombre(u)}? No podrá entrar hasta que lo reactives.`)) return;
    setOcupado(u._id);
    try {
      const { token } = await getSession();
      await axios.put(`${API_URL}/users/admin/${accion}/${u._id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUsuarios((prev) => prev.map((x) => (x._id === u._id ? { ...x, approved: accion === "approve" } : x)));
    } catch {
      setError(accion === "approve" ? "No se pudo aprobar. Inténtalo de nuevo." : "No se pudo suspender. Inténtalo de nuevo.");
    } finally {
      setOcupado(null);
    }
  };

  const pendientes = useMemo(() => (usuarios || []).filter((u) => !u.approved), [usuarios]);
  const filtrados = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    return (usuarios || []).filter((u) => !t || `${nombre(u)} ${u.email}`.toLowerCase().includes(t));
  }, [usuarios, buscar]);

  if (usuarios === null) return <div className="app-estado">Cargando accesos…</div>;

  return (
    <div style={{ marginBottom: 24 }}>
      {error && <p className="app-estado-error">{error}</p>}

      <section className="app-panel" style={{ padding: 16, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
          <FiUserCheck aria-hidden="true" />
          {pendientes.length > 0 ? `Pendientes de aprobar (${pendientes.length})` : "No hay cuentas pendientes"}
        </h2>
        {pendientes.length === 0 && (
          <p style={{ margin: "6px 0 0", opacity: 0.7 }}>Cuando alguien se registre, aparecerá aquí para aprobarlo.</p>
        )}
        {pendientes.map((u) => (
          <div key={u._id} style={fila}>
            <div style={{ minWidth: 0 }}>
              <strong>{nombre(u)}</strong>
              <div style={{ fontSize: 13, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis" }}>
                {u.email}
                {fecha(u) && ` · se registró el ${fecha(u)}`}
              </div>
            </div>
            <button
              type="button"
              className="app-btn app-btn-sm"
              disabled={ocupado === u._id}
              onClick={() => cambiar(u, "approve")}
            >
              <FiCheck aria-hidden="true" /> {ocupado === u._id ? "Aprobando…" : "Aprobar"}
            </button>
          </div>
        ))}
      </section>

      <section className="app-panel" style={{ padding: 16 }}>
        <button
          type="button"
          onClick={() => setVerTodos((v) => !v)}
          style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "inherit", fontSize: 18, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}
          aria-expanded={verTodos}
        >
          <FiUserX aria-hidden="true" /> Activar o suspender clientes {verTodos ? "▴" : "▾"}
        </button>
        {verTodos && (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 4px" }}>
              <FiSearch aria-hidden="true" />
              <input
                className="app-input"
                type="search"
                placeholder="Buscar por nombre o email"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                style={{ flex: 1 }}
              />
            </label>
            {filtrados.map((u) => (
              <div key={u._id} style={fila}>
                <div style={{ minWidth: 0 }}>
                  <strong>{nombre(u)}</strong>{" "}
                  <span
                    style={{
                      fontSize: 12,
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: u.approved ? "rgba(76,175,106,.18)" : "rgba(220,80,80,.15)",
                      color: u.approved ? "#2f8a4c" : "#c0392b",
                    }}
                  >
                    {u.approved ? "Activo" : "Suspendido o pendiente"}
                  </span>
                  <div style={{ fontSize: 13, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                </div>
                {u.approved ? (
                  <button
                    type="button"
                    className="app-btn app-btn-quiet app-btn-sm"
                    disabled={ocupado === u._id}
                    onClick={() => cambiar(u, "suspend")}
                  >
                    Suspender
                  </button>
                ) : (
                  <button
                    type="button"
                    className="app-btn app-btn-sm"
                    disabled={ocupado === u._id}
                    onClick={() => cambiar(u, "approve")}
                  >
                    Reactivar
                  </button>
                )}
              </div>
            ))}
            {filtrados.length === 0 && <p style={{ opacity: 0.7 }}>Nadie coincide con la búsqueda.</p>}
          </>
        )}
      </section>
    </div>
  );
}
